import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  EmployeeAvatar,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Tabs,
  useToast,
} from "../../design-system";
import { Icon } from "../../design-system/Icon";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { formatInstant } from "../../helpers/datetime";
import {
  getExcessWorkdays,
  getWorkdayIncidents,
  validateWorkdayIncident,
} from "../../helpers/kronaly_helper";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const CLASSIFICATION_LABELS = {
  authorized: "Exceso autorizado",
  unauthorized: "Exceso no autorizado",
  pending_review: "Pendiente de revisión",
  incident_justified: "Incidencia justificada",
};

const CLASSIFICATION_TONES = {
  authorized: "success",
  unauthorized: "danger",
  pending_review: "warning",
  incident_justified: "info",
};

function formatMinutes(m) {
  if (m == null) return "—";
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min} min`;
  return min > 0 ? `${h} h ${min} min` : `${h} h`;
}

function formatDate(ymd) {
  if (!ymd) return "—";
  // Fecha-solo: parseo con componentes LOCALES (independiente de zona horaria).
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function ExcesosJornada() {
  document.title = "Excesos de jornada | Kronaly";
  const toast = useToast();
  const { isManager, loading: membershipLoading, timezone } = useCurrentMembership();

  const [tab, setTab] = useState("all");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [search, setSearch] = useState("");

  const [data, setData] = useState({ items: [], stats: {} });
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationComment, setValidationComment] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;

      const [excessRes, incidentsRes] = await Promise.all([
        getExcessWorkdays(params),
        getWorkdayIncidents({ validated: "false", from: dateFrom, to: dateTo }),
      ]);

      setData(unwrap(excessRes) || { items: [], stats: {} });
      setIncidents(Array.isArray(unwrap(incidentsRes)) ? unwrap(incidentsRes) : []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "Error al cargar datos.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.items
      .filter((r) => {
        if (tab === "all") return true;
        return r.excess_classification === tab;
      })
      .filter((r) => {
        if (!q) return true;
        const haystack = [r.employee_name, r.employee_position, r.work_date]
          .filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      });
  }, [data.items, tab, search]);

  const handleValidateIncident = async (incident) => {
    setValidating(true);
    try {
      await validateWorkdayIncident(incident.id, { comment: validationComment || null });
      toast.success("Incidencia validada");
      setSelected(null);
      setValidationComment("");
      load();
    } catch (e) {
      toast.error("Error", typeof e === "string" ? e : e?.message);
    } finally {
      setValidating(false);
    }
  };

  if (membershipLoading) return <div className="kr-page"><Spinner /></div>;
  if (!isManager) {
    return (
      <div className="kr-page">
        <EmptyState icon="Lock" title="Sin acceso" description="Solo managers pueden ver esta sección." />
      </div>
    );
  }

  const tabs = [
    { id: "all", label: "Todos", count: data.items.length },
    { id: "pending_review", label: "Pendientes", count: data.stats.pending_review || 0 },
    { id: "unauthorized", label: "No autorizados", count: data.stats.unauthorized || 0 },
    { id: "authorized", label: "Autorizados", count: data.stats.authorized || 0 },
    { id: "incident_justified", label: "Con incidencia", count: data.stats.incident_justified || 0 },
  ];

  const cols = "1.8fr 1fr 0.8fr 0.8fr 1.4fr";

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Control de jornada"
        title="Excesos de jornada"
        description="Empleados que han trabajado más allá de su jornada ordinaria."
        actions={
          incidents.length > 0 ? (
            <Badge tone="warning" variant="solid">
              {incidents.length} incidencia{incidents.length > 1 ? "s" : ""} pendiente{incidents.length > 1 ? "s" : ""}
            </Badge>
          ) : null
        }
      />

      {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}

      <Card padded={false}>
        {/* Filters */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ width: 150 }}
          />
          <span style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>a</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ width: 150 }}
          />
          <div style={{ flex: 1, minWidth: 160 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar empleado..."
              icon="Search"
            />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: "8px 16px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
          <Tabs tabs={tabs} value={tab} onChange={setTab} />
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 40, textAlign: "center" }}><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40 }}>
            <EmptyState icon="CheckCircle2" title="Sin excesos" description="No hay excesos de jornada en el periodo seleccionado." />
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{
              display: "grid", gridTemplateColumns: cols, gap: 12,
              padding: "10px 20px", background: "var(--kr-neutral-50)",
              borderBottom: "1px solid var(--kr-border)",
              fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em",
              fontWeight: 600, color: "var(--kr-fg-muted)",
            }}>
              <span>Empleado</span>
              <span>Fecha</span>
              <span>Exceso</span>
              <span>Trabajado</span>
              <span>Clasificación</span>
            </div>
            {/* Rows */}
            {filtered.map((row) => (
              <div
                key={row.workday_record_id}
                onClick={() => setSelected(row)}
                style={{
                  display: "grid", gridTemplateColumns: cols, gap: 12,
                  padding: "12px 20px", borderBottom: "1px solid var(--kr-border-subtle)",
                  cursor: "pointer", alignItems: "center",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--kr-bg-subtle)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <EmployeeAvatar employeeId={row.employee_profile_id} hasAvatar={true} name={row.employee_name || "?"} size={28} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row.employee_name || "—"}
                    </div>
                    {row.employee_position && (
                      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{row.employee_position}</div>
                    )}
                  </div>
                </div>
                <span style={{ fontSize: 13, color: "var(--kr-fg-primary)" }}>{formatDate(row.work_date)}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-danger-600)" }}>+{formatMinutes(row.excess_minutes)}</span>
                <span style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>{formatMinutes(row.worked_minutes)}</span>
                <div>
                  <Badge tone={CLASSIFICATION_TONES[row.excess_classification] || "neutral"} variant="soft">
                    {CLASSIFICATION_LABELS[row.excess_classification] || row.excess_classification}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Pending incidents section */}
      {incidents.length > 0 && (
        <div className="kr-mt-4">
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--kr-fg-strong)", marginBottom: 12 }}>
            <Icon name="Flag" size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Incidencias pendientes de validación
          </h3>
          <Card padded={false}>
            {incidents.map((inc) => (
              <div
                key={inc.id}
                onClick={() => setSelected({ _isIncident: true, ...inc })}
                style={{
                  padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)",
                  cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--kr-bg-subtle)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
              >
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13, color: "var(--kr-fg-strong)" }}>
                    {inc.employee_name || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
                    {inc.type_label} · {formatDate(inc.work_date)}
                  </div>
                  {inc.description && (
                    <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
                      {inc.description}
                    </div>
                  )}
                </div>
                <Icon name="ChevronRight" size={16} color="var(--kr-fg-muted)" />
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Detail drawer */}
      <Drawer
        open={!!selected}
        onClose={() => { setSelected(null); setValidationComment(""); }}
        title={selected?._isIncident ? "Incidencia de jornada" : "Detalle de exceso"}
        size="md"
        footer={
          selected?._isIncident && !selected?.validated_by ? (
            <>
              <Button
                variant="primary"
                onClick={() => handleValidateIncident(selected)}
                disabled={validating}
              >
                {validating && <Spinner size={12} color="#fff" />}
                Validar incidencia
              </Button>
            </>
          ) : null
        }
      >
        {selected && !selected._isIncident && (
          <div style={{ padding: 16 }}>
            <dl style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", fontSize: 13 }}>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Empleado</dt><dd style={{ fontWeight: 500 }}>{selected.employee_name}</dd></div>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Fecha</dt><dd>{formatDate(selected.work_date)}</dd></div>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Tiempo trabajado</dt><dd>{formatMinutes(selected.worked_minutes)}</dd></div>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Exceso</dt><dd style={{ color: "var(--kr-danger-600)", fontWeight: 600 }}>+{formatMinutes(selected.excess_minutes)}</dd></div>
              <div style={{ gridColumn: "span 2" }}>
                <dt style={{ color: "var(--kr-fg-muted)", fontSize: 11, marginBottom: 4 }}>Clasificación</dt>
                <Badge tone={CLASSIFICATION_TONES[selected.excess_classification] || "neutral"} variant="soft">
                  {CLASSIFICATION_LABELS[selected.excess_classification] || selected.excess_classification}
                </Badge>
              </div>
              {selected.incident && (
                <div style={{ gridColumn: "span 2", borderTop: "1px solid var(--kr-border-subtle)", paddingTop: 12 }}>
                  <dt style={{ color: "var(--kr-fg-muted)", fontSize: 11, marginBottom: 4 }}>Incidencia asociada</dt>
                  <dd>
                    <span style={{ fontWeight: 500 }}>{selected.incident.type_label}</span>
                    {selected.incident.description && (
                      <p style={{ margin: "4px 0 0", color: "var(--kr-fg-muted)", fontSize: 12 }}>{selected.incident.description}</p>
                    )}
                    {selected.incident.validated_by ? (
                      <Badge tone="success" variant="soft" style={{ marginTop: 6 }}>Validada</Badge>
                    ) : (
                      <Badge tone="warning" variant="soft" style={{ marginTop: 6 }}>Pendiente de validación</Badge>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
        {selected?._isIncident && (
          <div style={{ padding: 16 }}>
            <dl style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", fontSize: 13 }}>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Empleado</dt><dd style={{ fontWeight: 500 }}>{selected.employee_name}</dd></div>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Fecha</dt><dd>{formatDate(selected.work_date)}</dd></div>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Tipo</dt><dd style={{ fontWeight: 500 }}>{selected.type_label}</dd></div>
              <div><dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Reportada</dt><dd>{formatInstant(selected.reported_at, timezone)}</dd></div>
              {selected.description && (
                <div style={{ gridColumn: "span 2" }}>
                  <dt style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>Descripción</dt>
                  <dd style={{ whiteSpace: "pre-wrap" }}>{selected.description}</dd>
                </div>
              )}
            </dl>
            {!selected.validated_by && (
              <div style={{ marginTop: 16 }}>
                <Field label="Comentario de validación (opcional)">
                  <Input
                    type="textarea"
                    rows={2}
                    value={validationComment}
                    onChange={(e) => setValidationComment(e.target.value)}
                    placeholder="Añadir comentario..."
                    maxLength={2000}
                  />
                </Field>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default ExcesosJornada;
